import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Stack,
  TextField,
  Button,
  Box,
  Alert,
  Tooltip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ImageIcon from '@mui/icons-material/Image';
import { saveAs } from 'file-saver';
import { usePdf } from '../../context/PdfContext';
import { getPdfPageCount, parsePageRanges } from '../../utils/pdfManipulation';
import { renderPdfPagesToImages, ImageFormat } from '../../utils/pdfImageExport';

const baseName = (name: string) => name.replace(/\.pdf$/i, '');
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const PdfImageExportPanel: React.FC = () => {
  const { files, currentFileId } = usePdf();

  const currentFile = files.find((f) => f.id === currentFileId);
  const currentIsPdf = currentFile?.fileType === 'pdf';

  const [pageCount, setPageCount] = useState<number | null>(null);
  const [range, setRange] = useState('');
  const [format, setFormat] = useState<ImageFormat>('png');
  const [scale, setScale] = useState(2);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setPageCount(null);
    if (currentIsPdf && currentFile) {
      getPdfPageCount(currentFile.file)
        .then((count) => { if (!cancelled) setPageCount(count); })
        .catch(() => { if (!cancelled) setPageCount(null); });
    }
    return () => { cancelled = true; };
  }, [currentIsPdf, currentFile]);

  const hasAnyPdf = files.some((f) => f.fileType === 'pdf');
  if (!hasAnyPdf) return null;

  const handleExport = async () => {
    if (!currentFile || !pageCount) return;
    setError(null);
    setSuccess(null);
    setBusy(true);
    setProgress(null);
    try {
      const pages = parsePageRanges(range.trim() || `1-${pageCount}`, pageCount).map((i) => i + 1);
      const results = await renderPdfPagesToImages(
        currentFile.file,
        pages,
        { scale, format },
        (done, total) => setProgress({ done, total })
      );

      const ext = format === 'jpeg' ? 'jpg' : 'png';
      const name = baseName(currentFile.file.name);

      if (results.length === 1) {
        saveAs(results[0].blob, `${name}-page-${results[0].pageNumber}.${ext}`);
      } else {
        for (const r of results) {
          saveAs(r.blob, `${name}-page-${r.pageNumber}.${ext}`);
          await delay(200);
        }
      }
      setSuccess(`Exported ${results.length} page${results.length === 1 ? '' : 's'} as ${format.toUpperCase()}.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Image export failed.');
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <Accordion disableGutters sx={{ mt: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
          🖼️ Export Pages as Images
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          {success && <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>}

          {!currentIsPdf ? (
            <Typography variant="caption" color="text.secondary">
              Select a PDF file to export pages as images.
            </Typography>
          ) : (
            <>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Pages{pageCount ? ` (current PDF: ${pageCount} pages)` : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Leave blank to export every page. Example: 1-3, 5, 8-10
                </Typography>
                <TextField
                  size="small"
                  placeholder="e.g. 1-3, 5 (blank = all pages)"
                  value={range}
                  onChange={(e) => setRange(e.target.value)}
                  disabled={busy}
                  fullWidth
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>Format</Typography>
                <ToggleButtonGroup
                  value={format}
                  exclusive
                  size="small"
                  onChange={(_e, v) => v && setFormat(v)}
                  disabled={busy}
                >
                  <ToggleButton value="png">PNG</ToggleButton>
                  <ToggleButton value="jpeg">JPEG</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>Resolution</Typography>
                <ToggleButtonGroup
                  value={scale}
                  exclusive
                  size="small"
                  onChange={(_e, v) => v && setScale(v)}
                  disabled={busy}
                >
                  <ToggleButton value={1}>Screen (1x)</ToggleButton>
                  <ToggleButton value={2}>High (2x)</ToggleButton>
                  <ToggleButton value={3}>Print (3x)</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Box>
                <Tooltip title={!pageCount ? 'Loading page count…' : ''}>
                  <span>
                    <Button
                      variant="outlined"
                      startIcon={busy ? <CircularProgress size={16} /> : <ImageIcon />}
                      onClick={handleExport}
                      disabled={busy || !pageCount}
                    >
                      {busy && progress ? `Exporting page ${progress.done} of ${progress.total}…` : 'Export Images'}
                    </Button>
                  </span>
                </Tooltip>
                {!busy && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Multiple pages download individually, one file per page.
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default PdfImageExportPanel;
